/* eslint-disable @typescript-eslint/naming-convention */
import { Nullable, StrictOmit } from '@enmon/commons';
import { MeterDetailThermal } from '@enmon/commons/lib/enmon';
import { Form } from 'antd';
import { FormItemProps } from 'components/Form/FormItem';
import { ThermalMeterDetailField } from 'components/Form/meter_details/thermal/constants';
import { ThermalDetails } from 'components/Form/meter_details/thermal/ThermalColdDetails';
import { VFC } from 'react';
import { useReplaceMeterStore } from 'routes/meter_replacement/useStore';

export type MeterThermalDetails = StrictOmit<MeterDetailThermal, 'flowrate' | 'flange'> & {
  flowrate?: {
    value: number | null;
    unit: string | null;
  } | null;
};

export interface MeterThermalDetailsProps extends MeterThermalDetails {
  onFormItemReset: (name: FormItemProps['name']) => void;
}

export const ThermalMeterDetails: VFC<MeterThermalDetailsProps> = props => {
  const { orientation, flowrate, layLength, diameterNominal, resistance, installationPosition, onFormItemReset } =
    props;

  const form = Form.useFormInstance();
  const flowrateUnit = Form.useWatch<Nullable<string>>(['detail', 'flowrate', 'unit'], form) ?? flowrate?.unit;
  const flowrateValue = Form.useWatch<Nullable<number>>(['detail', 'flowrate', 'value'], form) ?? flowrate?.value;

  const { setDetailsStepValues, detail } = useReplaceMeterStore();

  const handleFlowrateReset = () => {
    const emptyFlowrateValue = {
      detail: { ...detail, flowrate: { value: null, unit: null } } as MeterThermalDetails,
    };

    form.setFieldsValue({ detail: { flowrate: { value: null, unit: null } } });

    setDetailsStepValues(emptyFlowrateValue);
  };

  return (
    <ThermalDetails
      itemsConfig={{
        [ThermalMeterDetailField.Orientation]: {
          formItemProps: { initialValue: orientation, isSemiRequired: true, onResetField: onFormItemReset },
        },
        [ThermalMeterDetailField.Flowrate]: {
          formItemProps: {
            isSemiRequired: true,
            onResetField: handleFlowrateReset,
          },
          additionalConfig: {
            flowrateUnit,
            flowrateValue,
          },
        },
        [ThermalMeterDetailField.LayLength]: {
          formItemProps: { initialValue: layLength, isSemiRequired: true, onResetField: onFormItemReset },
        },
        [ThermalMeterDetailField.DiameterNominal]: {
          formItemProps: { initialValue: diameterNominal, isSemiRequired: true, onResetField: onFormItemReset },
        },
        [ThermalMeterDetailField.Resistance]: {
          formItemProps: { initialValue: resistance, isSemiRequired: true, onResetField: onFormItemReset },
        },
        [ThermalMeterDetailField.InstallationPosition]: {
          formItemProps: {
            initialValue: installationPosition,
            isSemiRequired: true,
            onResetField: onFormItemReset,
          },
        },
      }}
    />
  );
};
